/**
 * @file CorporateOfferConfirmation
 * @description
 * Displays corporate details and associated offers for confirmation.
 * Includes corporate information, additional details, and selected offer summary.
 */

import React, { useState } from 'react';
import { Row, Col, Card, Divider, Typography } from 'antd';
import { useTranslation } from 'soxo-bootstrap-core';
import SelectOffers from '../select-offers/select-offers';
import AppLoader from '../../components/app-loader';

const { Text } = Typography;

/**
 * @component InfoItem
 * @description
 * A reusable component that displays a label and corresponding value.
 *
 * @param {Object} props
 * @param {string} props.label - The label text to display.
 * @param {string|number} props.value - The value text to display.
 */
const InfoItem = ({ label, value }) => (
    <div className="info-item">
        <span className="label">
            <strong>{label}</strong>
        </span>
        <Text type="secondary">{value || ''}</Text>
    </div>
);

/**
 * @component CorporateOfferConfirmation
 * @description
 * Displays corporate details in a structured layout with an offer confirmation section.
 *
 * @param {Object} props
 * @param {Object} props.corporateDetails - Corporate details object containing company information.
 * @param {Array} props.selectedOffers - Array of currently selected offers.
 * @param {Function} props.setSelectedOffers - Callback to update selected offers.
 */

const CorporateOfferConfirmation = ({ corporateDetails, selectedOffers, setSelectedOffers }) => {
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    return (
        <>
        <AppLoader visible={loading} />
        <div className="confirm-info-container">
            <Row gutter={[16, 16]}>
                {/* Left Section */}
                <Col xs={24} lg={12}>
                    <Card className="info-card" title="Corporate Details">
                        {/* Row 1 */}
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('CODE')} value={corporateDetails.cc_code} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('MODE')} value={corporateDetails.mode} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('PLACE')} value={corporateDetails.place} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('ZIP CODE')} value={corporateDetails.zipCode} />
                            </Col>
                        </Row>

                        {/* Row 2 */}
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={24} md={12}>
                                <InfoItem label={t('COMPANY NAME')} value={corporateDetails.cc_desc} />
                            </Col>
                        </Row>

                        {/* Row 3 */}
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={12}>
                                <InfoItem label={t('COMPANY ADDRESS 1')} value={corporateDetails.cc_add1} />
                            </Col>
                            <Col xs={24} sm={12} md={12}>
                                <InfoItem label={t('COMPANY ADDRESS 2')} value={corporateDetails.cc_add2} />
                            </Col>
                        </Row>

                        <Divider className="custom-divider" />

                        {/* Row 4 */}
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('MOBILE NUMBER')} value={corporateDetails.cc_phone} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('EMAIL ID')} value={corporateDetails.cc_email} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('REG NO.1')} value={corporateDetails.cc_regno1} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label={t('REG NO.2')} value={corporateDetails.cc_regno2} />
                            </Col>

                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label="ACCOUNT CODE" value={corporateDetails.cc_acpostcode} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label="SI NO." value={corporateDetails.cc_slno} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label="CUSTOMER TYPE" value={corporateDetails.cc_type} />
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <InfoItem label="ACTIVE" value={corporateDetails.cc_active} />
                            </Col>
                        </Row>

                        <Divider className="custom-divider" />

                        {/* Row 5 */}
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={8}>
                                <InfoItem label="ALLOW ID CARD" value={corporateDetails.allowIdCard} />
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <InfoItem label="REFERRER" value={corporateDetails.referrer} />
                            </Col>
                        </Row>

                        <Divider className="custom-divider" />

                        <InfoItem label="REMARKS" value={corporateDetails.cc_remarks} />
                    </Card>
                </Col>

                {/* Right Section */}
                <Col xs={24} lg={12}>
                    <Card className="info-card" title="Corporate Extra Details">
                        <InfoItem label="OFFER DETAILS" value={corporateDetails.cc_styledcontent} />
                        {/* <Divider className="custom-divider" /> */}
                        {/* <InfoItem label="PAYMENT TERMS" value={extraDetails.paymentTerms} /> */}
                    </Card>
                </Col>
            </Row>

            <SelectOffers
                corporateDetails={corporateDetails}
                onSelectionChange={setSelectedOffers}
                showOnlySelected
                initialSelectedOffers={selectedOffers}
                confirmationMode
            />
        </div>
        </>
    );
};

export default CorporateOfferConfirmation;
